import { inject, Injectable } from '@angular/core';
import Keycloak from 'keycloak-js';
import { filter, firstValueFrom, timer } from 'rxjs';
import { ApolloService, getUser_users_by_pk } from '../../gql-generated/generated';
import { ConnectionService, HasuraRole } from './connection.service';
import { OrganizationService } from './organization.service';
import { ApiService } from './api.service';

@Injectable({
    providedIn: 'root',
})
export class UserService {
    /**
     * User profile. Only available after user is authenticated
     */
    profile!: getUser_users_by_pk;

    /**
     * Insufficient access
     */
    insufficientAccess = false;

    /**
     * User intitials based on first_name and last_name properties
     */
    initials?: string;

    /**
     * Keycloak
     */
    private readonly keycloak = inject(Keycloak);

    constructor(
        private api: ApiService,
        private apollo: ApolloService,
        private connection: ConnectionService,
        private organization: OrganizationService,
    ) {
        // Wait for connection state to be authenticated
        this.api.sync().then(() => {
            firstValueFrom(
                this.connection.state.pipe(filter((state) => state === 'authenticated')),
            ).then(async () => {
                // Attempt to find user with matching keycloak_id
                const keycloakUserId = (await this.keycloak.loadUserProfile()).id;

                if (keycloakUserId) {
                    const user = await firstValueFrom(this.apollo.getUser({ id: keycloakUserId }));
                    if (user.data?.users_by_pk) {
                        this.profile = user.data.users_by_pk;
                        this.initials = `${this.profile.first_name?.[0]}${this.profile.last_name?.[0]}`;

                        // Find roles user has acces to and choose role that grants the most features
                        const allowedRoles = this.keycloak.tokenParsed!['https://hasura.io/jwt/claims']?.['x-hasura-allowed-roles'] as string[];

                        // Determine best role for user. Use 'admin' if available and otherwise the first allowed role
                        this.connection.role.next(allowedRoles.includes('admin') ? 'admin' : allowedRoles[0] as HasuraRole);

                        // Update UI/UX based on current organization of user
                        if (this.profile.Organization) {
                            this.organization.setOrganization(this.profile.Organization);
                        } else {
                            // Apply default styling if no organization is set
                            this.organization.applyColors({ name: 'default' } as any);
                        }
                    }
                }
            });
            // Change status after 3 seconds to avoid flashing warning when user is redirected to login
        }).catch(() => setTimeout(() => {
            this.insufficientAccess = true;
        }, 3000));

        // Refresh token 120 seconds before it expires. Do this check every 60 seconds
        timer(60000, 60000).subscribe(() => this.keycloak.updateToken(120));
    }
}
